import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ShoppingCart, Building2, Plus, RefreshCw, Search, Loader2, X,
  Send, CheckCircle2, XCircle, Truck, Zap, Edit2, Trash2, FileText,
  Printer, PackagePlus, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

// Phase 4 · 발주관리 (자동 발주 초안 / 공급처 관리)

const PO_LABEL = { draft:'발주 초안', review:'검토중', approved:'승인완료',
  ordered:'발주완료', partially_received:'부분입고', received:'입고완료', cancelled:'취소' };
const PO_CLS = {
  draft:              'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  review:             'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
  approved:           'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300',
  ordered:            'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/30 dark:text-purple-300',
  partially_received: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-300',
  received:           'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  cancelled:          'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:border-white/10',
};
const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const fmt = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };

// A4-1G-2D · variant_key rename helper.  Mirrors backend `_variant_key`:
//   canonical = sorted "k=v" joined by "|", empty → "default".
function _canonicalVk(opt) {
  if (!opt || typeof opt !== 'object') return 'default';
  const keys = Object.keys(opt).filter((k) => k && opt[k] !== null && opt[k] !== '');
  if (!keys.length) return 'default';
  keys.sort();
  return keys.map((k) => `${k}=${opt[k]}`).join('|');
}
// A4-1G-2D · Compute {old_variant_key: new_variant_key} for a positional
//   options rename (group index-by-index, value index-by-index).  Only used
//   client-side for supplier price re-linking after the backend migration.
function _computeVkRenameMap(oldOptions, newOptions) {
  const nameRename = {};                             // old_group_name → new_group_name
  const labelRename = {};                            // "old_group_name||old_label" → new_label
  const len = Math.min(oldOptions.length, newOptions.length);
  for (let gi = 0; gi < len; gi++) {
    const og = oldOptions[gi] || {}, ng = newOptions[gi] || {};
    const on = (og.name || '').trim(), nn = (ng.name || '').trim();
    if (!on || !nn) continue;
    nameRename[on] = nn;
    const ov = og.values || [], nv = ng.values || [];
    const vlen = Math.min(ov.length, nv.length);
    for (let vi = 0; vi < vlen; vi++) {
      const ol = ((ov[vi] || {}).label || '').trim();
      const nl = ((nv[vi] || {}).label || '').trim();
      if (ol && nl) labelRename[`${on}||${ol}`] = nl;
    }
  }
  const out = {};
  const renameVk = (oldVk) => {
    if (!oldVk || oldVk === 'default') return oldVk;
    const parts = oldVk.split('|');
    const opt = {};
    for (const p of parts) {
      const idx = p.indexOf('=');
      if (idx < 0) continue;
      const k = p.slice(0, idx), v = p.slice(idx + 1);
      const nk = nameRename[k] || k;
      const nv = labelRename[`${k}||${v}`] || v;
      opt[nk] = nv;
    }
    return _canonicalVk(opt);
  };
  // Enumerate all combos (unused — caller supplies concrete old_vks). Provide
  // as a function-style map: apply on demand.  We return an object where any
  // seen old_vk maps to its new_vk; caller invokes `.__apply` for arbitrary vks.
  out.__apply = renameVk;
  // Convenience: also pre-fill for empty enumeration hint.
  return new Proxy(out, {
    get(target, prop) {
      if (prop in target) return target[prop];
      if (typeof prop !== 'string') return undefined;
      return renameVk(prop);
    },
  });
}

export default function PurchaseOrderSection() {
  const [subTab, setSubTab] = useState('po'); // po | suppliers
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/seller/dashboard/pending-approvals');
        setPendingCount(r.data?.auto_pending || 0);
      } catch {}
    })();
  }, [subTab]);
  return (
    <div className="space-y-4" data-testid="seller-po-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>발주관리</h2>
          <p className="text-xs text-gray-500 mt-1">
            재고 부족 상품을 자동 탐지해 발주 초안을 만듭니다. 판매자 승인 전까지 공급처로 어떤 것도 전송되지 않습니다.
          </p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setSubTab('po')} className={`px-3 py-2 rounded-lg text-xs font-black ${subTab === 'po' ? 'bg-[#FF3B30] text-white' : 'border border-black/10 dark:border-white/10'}`} data-testid="po-tab-po">발주서</button>
          <button onClick={() => setSubTab('suppliers')} className={`px-3 py-2 rounded-lg text-xs font-black ${subTab === 'suppliers' ? 'bg-[#FF3B30] text-white' : 'border border-black/10 dark:border-white/10'}`} data-testid="po-tab-suppliers">공급처</button>
        </div>
      </div>
      {pendingCount > 0 && subTab === 'po' && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="po-pending-banner">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="text-amber-600 dark:text-amber-300 shrink-0" size={18}/>
            <p className="text-sm font-bold text-amber-800 dark:text-amber-200">
              승인이 필요한 자동 발주 초안이 <b className="text-lg">{pendingCount}건</b> 있습니다.
            </p>
          </div>
          <button onClick={() => {
              const btn = document.querySelector('[data-testid="po-card-draft"]');
              btn?.click();
            }} className="px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-black" data-testid="po-pending-goto">
            확인하기
          </button>
        </div>
      )}
      {subTab === 'po' ? <POList /> : <SuppliersTab />}
    </div>
  );
}

function POList() {
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Phase 5.3B — read ?sku=... from URL and prefill search
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sku = params.get('sku');
      if (sku) setQ(sku);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [detail, setDetail] = useState(null); // po_id
  const [suggestOpen, setSuggestOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab && tab !== 'all') params.status = tab;
      if (q) params.q = q;
      const r = await api.get('/seller/purchase-orders', { params });
      setRows(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setLoading(false); }
  }, [tab, q]);
  useEffect(() => { load(); }, [load]);

  const autoDraft = async () => {
    try {
      const r = await api.post('/seller/purchase-orders/auto-draft');
      if (r.data?.count > 0) toast.success(`발주 초안 ${r.data.count}건이 생성되었습니다.`);
      else toast.info(r.data?.message || '발주가 필요한 상품이 없습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  const [scanResult, setScanResult] = useState(null);
  const [settings, setSettings] = useState({ reorder_auto_scan_enabled: true });
  useEffect(() => { (async () => {
    try { const r = await api.get('/seller/reorder-settings'); setSettings(r.data); } catch {}
  })(); }, []);
  const runScan = async () => {
    try {
      const r = await api.post('/seller/purchase-orders/scan');
      const res = r.data.result;
      setScanResult(res);
      toast.success(`검사 완료 · 부족 ${res.shortage_count}건, 신규 초안 ${res.new_drafts}건`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };
  const toggleAuto = async () => {
    const next = !settings.reorder_auto_scan_enabled;
    try {
      const r = await api.patch('/seller/reorder-settings', { reorder_auto_scan_enabled: next });
      setSettings({ reorder_auto_scan_enabled: r.data.reorder_auto_scan_enabled });
      toast.success(`자동 재고 검사 ${next ? 'ON' : 'OFF'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  return (
    <div className="space-y-3" data-testid="po-list">
      {/* Action bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={runScan} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black" data-testid="po-scan">
          <RefreshCw size={12}/> 재고 다시 검사
        </button>
        <button onClick={autoDraft} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black" data-testid="po-auto-draft">
          <Zap size={12}/> 재고 부족 자동 초안
        </button>
        <button onClick={() => setSuggestOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5" data-testid="po-open-suggest">
          <AlertTriangle size={12}/> 발주 필요 SKU
        </button>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="po-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/> 새로고침
        </button>
        <label className="ml-auto flex items-center gap-2 text-xs cursor-pointer" data-testid="po-auto-toggle">
          <input type="checkbox" checked={settings.reorder_auto_scan_enabled} onChange={toggleAuto}/>
          <span className="font-bold">자동 재고 검사 ON</span>
          <span className="text-[10px] text-gray-500">(실 발주 아님 · 초안만 자동)</span>
        </label>
      </div>

      {/* Scan result panel */}
      {scanResult && (
        <div className="rounded-2xl border-2 border-emerald-400/60 bg-emerald-50/60 dark:bg-emerald-500/5 p-3" data-testid="po-scan-result">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-black text-emerald-700 dark:text-emerald-300">재고 검사 결과 · {scanResult.scan_id}</p>
            <button onClick={() => setScanResult(null)} className="text-gray-400"><X size={12}/></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <div className="bg-white dark:bg-black/20 rounded p-2"><p className="text-gray-500">부족 SKU</p><p className="text-lg font-black">{scanResult.shortage_count}개</p></div>
            <div className="bg-white dark:bg-black/20 rounded p-2"><p className="text-gray-500">신규 발주 초안</p><p className="text-lg font-black text-[#FF3B30]">{scanResult.new_drafts}건</p></div>
            <div className="bg-white dark:bg-black/20 rounded p-2"><p className="text-gray-500">기존 발주로 충족</p><p className="text-lg font-black text-indigo-600">{scanResult.covered_by_existing}개</p></div>
            <div className="bg-white dark:bg-black/20 rounded p-2"><p className="text-gray-500">공급처 미등록</p><p className="text-lg font-black text-amber-600">{scanResult.missing_supplier?.length || 0}개</p></div>
          </div>
          {(scanResult.missing_supplier || []).length > 0 && (
            <div className="mt-2 space-y-1" data-testid="po-scan-missing">
              <p className="text-[10px] font-black uppercase text-amber-700">공급처 미등록 SKU</p>
              {scanResult.missing_supplier.map((m, i) => (
                <div key={i} className="flex justify-between text-[11px] py-0.5">
                  <span className="truncate">{m.product_name} · <span className="font-mono">{m.sku}</span></span>
                  <span className="text-gray-500">권장 {m.recommended}개</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Original action bar removed */}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="po-summary-cards">
        {['draft','review','approved','ordered','partially_received','received','cancelled'].map((k) => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-2xl border p-3 text-left ${tab === k ? PO_CLS[k] + ' border-2' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'}`} data-testid={`po-card-${k}`}>
            <p className="text-[10px] font-black uppercase tracking-wider">{PO_LABEL[k]}</p>
            <p className="text-2xl font-black mt-1">{counts?.[k] || 0}</p>
          </button>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2">
        <div className="flex flex-wrap gap-1">
          {['all','draft','approved','partially_received','received','cancelled'].map((k) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-black border ${tab === k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`} data-testid={`po-tab-${k}`}>
              {k === 'all' ? '전체' : PO_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="발주번호 · 공급처 · 상품명 · SKU"
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="po-search"/>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="po-empty">
          <ShoppingCart className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">발주 초안이 없습니다.</p>
          <p className="text-[11px] text-gray-500 mt-1">위의 [재고 부족 자동 초안] 을 눌러 부족 SKU 를 탐색해보세요.</p>
        </div>
      ) : (
        <>
          {/* PC table */}
          <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10" data-testid="po-table">
            <table className="w-full text-xs">
              <thead className="bg-black/[0.03] dark:bg-white/5">
                <tr className="text-left">
                  <th className="px-3 py-2 font-black">발주번호</th>
                  <th className="px-3 py-2 font-black">공급처</th>
                  <th className="px-3 py-2 font-black text-right">상품수</th>
                  <th className="px-3 py-2 font-black text-right">예상금액</th>
                  <th className="px-3 py-2 font-black">예상입고일</th>
                  <th className="px-3 py-2 font-black">상태</th>
                  <th className="px-3 py-2 font-black">액션</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.po_id} className="border-t border-black/5 dark:border-white/10" data-testid={`po-row-${r.po_id}`}>
                    <td className="px-3 py-2 font-mono font-bold">
                      {r.po_number}
                      {r.creation_source === 'auto_reorder' && (
                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[9px] font-black" data-testid={`po-badge-auto-${r.po_id}`}>⚡자동</span>
                      )}
                      {r.creation_source === 'manual' && (
                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 text-[9px] font-black">수동</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{r.supplier_snapshot?.name || '-'}</td>
                    <td className="px-3 py-2 text-right">{r.items?.length || 0}</td>
                    <td className="px-3 py-2 text-right font-bold">{won(r.total_amount)}</td>
                    <td className="px-3 py-2">{r.expected_arrival_date || '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${PO_CLS[r.status]}`} data-testid={`po-status-${r.po_id}`}>{r.status_label}</span>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => setDetail(r.po_id)} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`po-open-${r.po_id}`}>상세</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="po-cards">
            {rows.map((r) => (
              <div key={r.po_id} className="bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-3" data-testid={`po-mrow-${r.po_id}`}>
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <p className="font-mono font-black text-sm">{r.po_number}</p>
                    <p className="text-[11px] text-gray-500">{r.supplier_snapshot?.name || '-'}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black ${PO_CLS[r.status]}`}>{r.status_label}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[11px] py-2 bg-black/[0.02] dark:bg-white/[0.03] rounded-lg text-center mt-1">
                  <div><p className="text-[9px] text-gray-500">상품수</p><p className="font-bold">{r.items?.length || 0}</p></div>
                  <div><p className="text-[9px] text-gray-500">예상금액</p><p className="font-black">{won(r.total_amount)}</p></div>
                  <div><p className="text-[9px] text-gray-500">예상입고</p><p className="font-bold">{r.expected_arrival_date || '-'}</p></div>
                </div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => setDetail(r.po_id)} className="flex-1 py-1.5 rounded bg-[#FF3B30] text-white text-[11px] font-black" data-testid={`po-m-open-${r.po_id}`}>상세</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {detail && <PODetailModal poId={detail} onClose={() => setDetail(null)} onChanged={load}/>}
      {suggestOpen && <SuggestModal onClose={() => setSuggestOpen(false)} onDrafted={() => { setSuggestOpen(false); load(); }}/>}
    </div>
  );
}

function SuggestModal({ onClose, onDrafted }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/seller/inventory/reorder-suggestions');
        setGroups(r.data?.groups || []);
      } catch (e) { toast.error('실패'); }
      finally { setLoading(false); }
    })();
  }, []);
  const draftAll = async () => {
    setBusy(true);
    try {
      const r = await api.post('/seller/purchase-orders/auto-draft');
      toast.success(`발주 초안 ${r.data.count}건 생성`);
      onDrafted();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="po-suggest-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <p className="text-sm font-black">발주 필요 SKU 목록</p>
          <button onClick={onClose}><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          {loading ? <div className="text-center py-8 text-gray-400"><Loader2 size={14} className="animate-spin inline"/></div>
          : groups.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">모든 상품이 안전재고 이상입니다. 추가 발주가 필요하지 않습니다.</p>
          ) : groups.map((g, i) => (
            <div key={i} className="rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid={`po-suggest-group-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-sm">{g.supplier?.name || '-'}</p>
                <p className="text-xs font-black text-[#FF3B30]">{won(g.estimated_total)}</p>
              </div>
              <div className="space-y-1 text-[11px]">
                {g.items.map((it, j) => (
                  <div key={j} className="border-b border-black/5 dark:border-white/10 pb-1.5 mb-1.5 last:border-b-0 last:mb-0 last:pb-0" data-testid={`po-suggest-item-${j}`}>
                    <p className="font-bold truncate text-[12px]">{it.product_name} <span className="font-mono text-gray-500 ml-1">{it.sku}</span></p>
                    <div className="grid grid-cols-5 gap-1 mt-1 text-[10px]">
                      <div><p className="text-gray-500">미출고 주문</p><p className="font-black text-blue-700 dark:text-blue-300" data-testid={`po-suggest-unfulfilled-${j}`}>{it.unfulfilled_qty || 0}</p></div>
                      <div><p className="text-gray-500">판매가능</p><p className="font-black">{it.available_stock}</p></div>
                      <div><p className="text-gray-500">입고예정</p><p className="font-black text-amber-700 dark:text-amber-400">{it.expected_stock}</p></div>
                      <div><p className="text-gray-500">주문 부족</p><p className="font-black text-rose-700 dark:text-rose-400" data-testid={`po-suggest-order-short-${j}`}>{it.order_shortage || 0}</p></div>
                      <div><p className="text-gray-500">권장 발주</p><p className="font-black text-[#FF3B30]" data-testid={`po-suggest-recommended-${j}`}>{it.recommended}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {groups.length > 0 && (
            <button onClick={draftAll} disabled={busy} className="w-full py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40" data-testid="po-suggest-draft-all">
              {busy ? '생성 중…' : `공급처별 발주 초안 ${groups.length}건 자동 생성`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PODetailModal({ poId, onClose, onChanged }) {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editItems, setEditItems] = useState(null);
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/seller/purchase-orders/${poId}`);
      setPo(r.data);
      setEditItems(r.data.items || []);
      setMemo(r.data.memo || '');
    } catch (e) { toast.error('실패'); onClose(); }
    finally { setLoading(false); }
  }, [poId]);
  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => {
    setBusy(true);
    try {
      await api.patch(`/seller/purchase-orders/${poId}`, {
        items: editItems.filter((it) => Number(it.ordered_qty) > 0),
        memo: memo || null,
      });
      toast.success('저장되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!window.confirm('발주를 승인하시겠습니까? 승인 시 입고예정이 자동 생성됩니다. (공급처로 실제 전송은 하지 않습니다)')) return;
    setBusy(true);
    try {
      await api.post(`/seller/purchase-orders/${poId}/approve`);
      toast.success('승인되었습니다. 입고예정이 자동 생성되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    const reason = window.prompt('취소 사유를 입력하세요 (선택):') || '';
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/seller/purchase-orders/${poId}/cancel`, { reason });
      toast.success('취소되었습니다.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  if (!po && loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <Loader2 className="animate-spin text-white" size={24}/>
    </div>
  );
  if (!po) return null;

  const editable = po.status === 'draft' || po.status === 'review';
  const canApprove = editable;
  const canCancel = po.status !== 'received' && po.status !== 'cancelled';
  const items = editable ? editItems : po.items;
  const total = (items || []).reduce((s, it) => s + (Number(it.ordered_qty) || 0) * (Number(it.purchase_price) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="po-detail-modal">
      <div className="flex-1 bg-black/50" onClick={onClose}/>
      <div className="w-full max-w-3xl bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-[#0A0A0A] border-b border-black/10 dark:border-white/10 p-3 flex justify-between items-center z-10">
          <div>
            <p className="text-sm font-black flex items-center gap-1"><ShoppingCart size={14}/> {po.po_number}</p>
            <p className="text-[11px] text-gray-500">{po.supplier_snapshot?.name} · {po.expected_arrival_date || '-'}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${PO_CLS[po.status]}`}>{po.status_label}</span>
            <button onClick={onClose}><X size={14}/></button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="rounded-xl border border-black/10 dark:border-white/10" data-testid="po-detail-items">
            <table className="w-full text-xs">
              <thead className="bg-black/[0.03] dark:bg-white/5">
                <tr>
                  <th className="px-2 py-1 text-left">상품</th>
                  <th className="px-2 py-1 text-right">권장</th>
                  <th className="px-2 py-1 text-right">발주수량</th>
                  <th className="px-2 py-1 text-right">단가</th>
                  <th className="px-2 py-1 text-right">금액</th>
                  <th className="px-2 py-1 text-right">입고</th>
                  {editable && <th className="px-2 py-1 text-right">제외</th>}
                </tr>
              </thead>
              <tbody>
                {(items || []).map((it, i) => (
                  <tr key={i} className="border-t border-black/5 dark:border-white/10" data-testid={`po-item-${i}`}>
                    <td className="px-2 py-1">
                      <p className="font-bold truncate">{it.name_snapshot}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{it.sku || '-'}</p>
                      {it.context && (
                        <div className="grid grid-cols-5 gap-1 mt-1 text-[9px]" data-testid={`po-item-context-${i}`}>
                          <div><span className="text-gray-500">미출고</span> <b className="text-blue-700 dark:text-blue-300">{it.context.unfulfilled_qty || 0}</b></div>
                          <div><span className="text-gray-500">판매가능</span> <b>{it.context.available}</b></div>
                          <div><span className="text-gray-500">입고예정</span> <b className="text-amber-700 dark:text-amber-400">{it.context.expected}</b></div>
                          <div><span className="text-gray-500">주문부족</span> <b className="text-rose-700 dark:text-rose-400" data-testid={`po-item-order-short-${i}`}>{it.context.order_shortage || 0}</b></div>
                          <div><span className="text-gray-500">MOQ</span> <b>{it.context.moq || 1}</b></div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right text-gray-500 font-mono">{it.recommended_qty}</td>
                    <td className="px-2 py-1 text-right">
                      {editable ? (
                        <input type="number" min={0} value={it.ordered_qty} onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          const arr = [...editItems];
                          arr[i] = { ...arr[i], ordered_qty: v, line_total: v * (arr[i].purchase_price || 0) };
                          setEditItems(arr);
                        }} className="w-16 px-1 py-0.5 border border-black/10 dark:border-white/10 rounded text-right font-mono bg-transparent" data-testid={`po-item-qty-${i}`}/>
                      ) : <span className="font-mono font-bold">{it.ordered_qty}</span>}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{won(it.purchase_price)}</td>
                    <td className="px-2 py-1 text-right font-bold">{won((it.ordered_qty || 0) * (it.purchase_price || 0))}</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-600">{it.received_qty || 0}</td>
                    {editable && (
                      <td className="px-2 py-1 text-right">
                        <button onClick={() => {
                          const arr = [...editItems]; arr.splice(i, 1); setEditItems(arr);
                        }} className="text-rose-600" data-testid={`po-item-remove-${i}`}><Trash2 size={12}/></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-black/20 dark:border-white/20 bg-black/[0.02] dark:bg-white/[0.03]">
                  <td colSpan={4} className="px-2 py-2 text-right font-black">합계</td>
                  <td className="px-2 py-2 text-right font-black text-[#FF3B30]" data-testid="po-total-amount">{won(total)}</td>
                  <td colSpan={editable ? 2 : 1}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {editable && (
            <div>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">메모</p>
              <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2}
                className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="po-memo"/>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button onClick={() => setPreviewOpen(true)} className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="po-preview">
              <Printer size={12} className="inline mr-1"/>발주서 미리보기
            </button>
            {editable && (
              <button onClick={saveEdits} disabled={busy} className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="po-save">
                <Edit2 size={12} className="inline mr-1"/>저장
              </button>
            )}
            {canApprove && (
              <button onClick={approve} disabled={busy} className="px-3 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black" data-testid="po-approve">
                <CheckCircle2 size={12} className="inline mr-1"/>발주 승인
              </button>
            )}
            {canCancel && (
              <button onClick={cancel} disabled={busy} className="ml-auto px-3 py-2 rounded-lg border border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs font-bold" data-testid="po-cancel">
                <XCircle size={12} className="inline mr-1"/>발주 취소
              </button>
            )}
          </div>

          {po.history?.length > 0 && (
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-2" data-testid="po-history">
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">이력</p>
              {po.history.map((h, i) => (
                <div key={i} className="text-[11px] flex justify-between border-b border-black/5 dark:border-white/10 py-1 last:border-b-0">
                  <span className="font-bold">{h.event}</span>
                  <span className="text-gray-500">{fmt(h.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {previewOpen && <POPreview poId={poId} onClose={() => setPreviewOpen(false)}/>}
    </div>
  );
}

function POPreview({ poId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/seller/purchase-orders/${poId}/preview`); setData(r.data); }
      catch (e) { toast.error('실패'); onClose(); }
    })();
  }, [poId]);
  if (!data) return null;
  const po = data.purchase_order;
  const items = po.items || [];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="po-preview-modal">
      <div className="bg-white text-black rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 flex justify-between items-center">
          <p className="text-sm font-black">발주서 · {po.po_number}</p>
          <div className="flex gap-1">
            <button onClick={() => window.print()} className="px-2 py-1 rounded border border-black/10 text-xs font-bold"><Printer size={10} className="inline mr-1"/>인쇄</button>
            <button onClick={onClose}><X size={14}/></button>
          </div>
        </div>
        <div className="p-6 print:p-6 space-y-3" data-testid="po-preview-content">
          <h1 className="text-2xl font-black text-center">발 주 서</h1>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><b>발주번호:</b> <span className="font-mono">{po.po_number}</span></div>
            <div><b>발주일:</b> {fmt(po.created_at)}</div>
            <div><b>공급처:</b> {po.supplier_snapshot?.name}</div>
            <div><b>담당자:</b> {po.supplier_snapshot?.contact_name || '-'}</div>
            <div><b>전화:</b> {po.supplier_snapshot?.phone || '-'}</div>
            <div><b>예상입고일:</b> {po.expected_arrival_date || '-'}</div>
          </div>
          <table className="w-full border-collapse text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-1">번호</th>
                <th className="border p-1 text-left">상품명</th>
                <th className="border p-1">SKU</th>
                <th className="border p-1 text-right">수량</th>
                <th className="border p-1 text-right">단가</th>
                <th className="border p-1 text-right">금액</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="border p-1 text-center">{i + 1}</td>
                  <td className="border p-1">{it.name_snapshot}</td>
                  <td className="border p-1 font-mono">{it.sku || '-'}</td>
                  <td className="border p-1 text-right font-mono">{it.ordered_qty}</td>
                  <td className="border p-1 text-right font-mono">{won(it.purchase_price)}</td>
                  <td className="border p-1 text-right font-bold">{won(it.ordered_qty * it.purchase_price)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td colSpan={5} className="border p-1 text-right font-black">합계</td>
                <td className="border p-1 text-right font-black">{won(po.total_amount)}</td>
              </tr>
            </tfoot>
          </table>
          {po.memo && <div className="text-xs"><b>메모:</b> {po.memo}</div>}
          <p className="text-[10px] text-gray-500 text-center mt-4">※ 본 발주서는 LIVEMOA 내부 자료입니다. 공급처에 실제 전송되지 않았습니다.</p>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// A4-1G-2 · 매입처 관리 UI (기존 supplier foundation REUSE ONLY)
//   - 기존 GET /seller/suppliers, PATCH /seller/suppliers/{sid}, POST /seller/suppliers
//   - 기존 GET /seller/suppliers/{sid}/products (연결된 상품/공급가 조회) REUSE
//   - 기존 GET /seller/product-suppliers (전체 링크 → supplier 별 상품 수 count 용) REUSE
//   신규 DB / migration / hard-delete API 만들지 않는다.
// ═════════════════════════════════════════════════════════════════════════
function SuppliersTab() {
  const [rows, setRows] = useState([]);
  const [linkCount, setLinkCount] = useState({}); // supplier_id → 연결 상품 수 (unique product_id)
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [detailSid, setDetailSid] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 기존 API 그대로. active=true 기본 (deactivate 된 매입처는 별도 필터 없이 노출 유지).
      const [rS, rL] = await Promise.all([
        api.get('/seller/suppliers'),
        api.get('/seller/product-suppliers').catch(() => ({ data: { items: [] } })),
      ]);
      setRows(rS.data?.items || []);
      // supplier_id 별 고유 product_id 수 집계 (client-side · 신규 API 생성 없음).
      const map = {};
      for (const l of (rL.data?.items || [])) {
        const sid_ = l.supplier_id; const pid = l.product_id;
        if (!sid_ || !pid) continue;
        if (!map[sid_]) map[sid_] = new Set();
        map[sid_].add(pid);
      }
      const counts = {};
      for (const k of Object.keys(map)) counts[k] = map[k].size;
      setLinkCount(counts);
    } catch (e) { toast.error('매입처 조회 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const qLower = q.trim().toLowerCase();
  const filtered = qLower
    ? rows.filter((s) => (
        (s.name || '').toLowerCase().includes(qLower) ||
        (s.contact_name || '').toLowerCase().includes(qLower) ||
        (s.phone || '').toLowerCase().includes(qLower)
      ))
    : rows;

  return (
    <div className="space-y-3" data-testid="supplier-list">
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold flex-1">매입처 <span className="text-gray-400 font-normal">{filtered.length}건</span></p>
        <div className="relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="이름 · 담당자 · 전화번호"
            className="pl-6 pr-2 py-1.5 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent w-44 sm:w-56"
            data-testid="supplier-search"/>
        </div>
        <button onClick={() => setOpenCreate(true)}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black"
          data-testid="supplier-open-create">
          <Plus size={12}/> 매입처 등록
        </button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-400"><Loader2 size={14} className="animate-spin inline"/></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] rounded-2xl border border-dashed border-black/10">
          <Building2 className="mx-auto text-gray-300" size={28}/>
          <p className="mt-3 text-sm font-bold">등록된 매입처가 없습니다.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="supplier-cards">
          {filtered.map((s) => (
            <button key={s.supplier_id} type="button"
              onClick={() => setDetailSid(s.supplier_id)}
              className="text-left rounded-2xl border border-black/10 dark:border-white/10 p-3 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors"
              data-testid={`supplier-${s.supplier_id}`}>
              <div className="flex justify-between items-start gap-2">
                <p className="font-bold truncate flex-1">{s.name}</p>
                {s.is_active === false && (
                  <span className="text-[9px] font-black text-gray-400 flex-shrink-0">비활성</span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {s.contact_name || '담당자 미등록'} · {s.phone || '연락처 미등록'}
              </p>
              {s.kakao_id && <p className="text-[10px] text-gray-500">카카오톡 · {s.kakao_id}</p>}
              <div className="mt-2 flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 text-[10px] font-bold">
                  <PackagePlus size={10}/> 연결 상품 {linkCount[s.supplier_id] || 0}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
      {openCreate && <SupplierCreateModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }}/>}
      {detailSid && (
        <SupplierDetailModal
          sid={detailSid}
          onClose={() => setDetailSid(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

// A4-1G-2 · 신규 매입처 등록 modal (간소화 필드 · MOQ/발주단위/리드타임/안전재고 UI 제거).
//   기존 POST /seller/suppliers 그대로 사용. legacy 필드는 backend 에서 기본 0 저장 (기존 데이터 미영향).
function SupplierCreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name:'', contact_name:'', phone:'', kakao_id:'',
    email:'', address:'', address_detail:'', memo:'' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error('매입처명을 입력해주세요.'); return; }
    setBusy(true);
    try { await api.post('/seller/suppliers', f); toast.success('등록되었습니다.'); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };
  const FIELDS = [
    ['name','매입처명 *'],['contact_name','담당자명'],
    ['phone','전화번호'],['kakao_id','카카오톡 연락처/ID'],
    ['email','이메일'],['address','주소'],['address_detail','상세주소'],
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="supplier-create-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <p className="text-sm font-black">매입처 등록</p>
          <button onClick={onClose} aria-label="닫기"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-2">
          {FIELDS.map(([k,l]) => (
            <div key={k}>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">{l}</p>
              <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                data-testid={`supplier-field-${k}`}/>
            </div>
          ))}
          <div>
            <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">메모</p>
            <textarea value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="supplier-field-memo"/>
          </div>
          <button onClick={submit} disabled={busy}
            className="w-full py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40"
            data-testid="supplier-submit">
            {busy ? '등록 중…' : '매입처 등록'}
          </button>
        </div>
      </div>
    </div>
  );
}

// A4-1G-2 · 매입처 상세 modal.  기본정보 표시 + [수정] + 이 매입처에서 매입하는 상품 목록.
//   - GET /seller/suppliers/{sid}/products (기존 API REUSE) 로 links + supplier + product 요약 조회.
//   - PATCH /seller/suppliers/{sid} 로 정보 수정 (기존 API REUSE).
//   - Hard delete 없음.  is_active toggle 이 필요할 경우 PATCH is_active=false 로 deactivate (기존 구조 REUSE).
//   A4-1G-2A · 상품 행 인라인 편집.  상품명 = PUT /seller/products/{pid} · 매입처 상품명 = 각 variant 에
//   POST /seller/product-suppliers 로 supplier_product_name upsert · 공급가 = 각 variant purchase_price upsert.
//   옵션명 편집은 상품수정 modal (variant/option 구조 편집기) 로 안내 (DEFERRED — 여기서 임의 구조 수정 X).
export function SupplierDetailModal({ sid, onClose, onSaved }) {
  const [sup, setSup] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/seller/suppliers/${sid}/products`);
      setSup(data.supplier || null); setItems(data.items || []);
      setForm({
        name: data.supplier?.name || '',
        contact_name: data.supplier?.contact_name || '',
        phone: data.supplier?.phone || '',
        kakao_id: data.supplier?.kakao_id || '',
        email: data.supplier?.email || '',
        address: data.supplier?.address || '',
        address_detail: data.supplier?.address_detail || '',
        memo: data.supplier?.memo || '',
      });
    } catch (e) { toast.error(e?.response?.data?.detail || '매입처 조회 실패'); }
    finally { setLoading(false); }
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  // A4-1G-2A · 상품 행 인라인 편집 state.
  //   editingPid: 현재 편집 중인 product_id · productForm: { name, supplier_product_name, prices:{vk→n}, options:[...] }
  //   A4-1G-2D · options (색상/사이즈 그룹명, 옵션값 라벨) 도 편집 가능.  저장 시
  //     PUT /seller/products/{pid} 가 old→new variant_key migration 을 수행해서
  //     SKU / Barcode / 재고 / 매입처 연결 / 공급가가 그대로 유지된다.
  const [editingPid, setEditingPid] = useState(null);
  const [productForm, setProductForm] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const beginEditProduct = async (g) => {
    setEditingPid(g.product_id);
    // Snapshot the ORIGINAL options so we can compute old→new variant_key map on save.
    setProductForm({
      name: g.product_name || '',
      supplier_product_name: g.supplier_product_name || '',
      prices: Object.fromEntries(g.rows.map((r) => [r.vk, Number(r.price ?? 0)])),
      options: null,
      original_options: null,
      loading_options: true,
    });
    try {
      const { data } = await api.get(`/seller/products/${g.product_id}`);
      const opts = Array.isArray(data?.product?.options) ? data.product.options
                    : Array.isArray(data?.options) ? data.options : [];
      const cloned = opts.map((grp) => ({
        ...grp,
        values: (grp.values || []).map((v) => ({ ...v })),
      }));
      // Deep-freeze snapshot for rename map computation (shallow copy of arrays is enough).
      const snapshot = opts.map((grp) => ({
        name: grp.name, values: (grp.values || []).map((v) => ({ label: v.label })),
      }));
      setProductForm((prev) => prev ? {
        ...prev, options: cloned, original_options: snapshot, loading_options: false,
      } : prev);
    } catch (e) {
      setProductForm((prev) => prev ? {
        ...prev, options: [], original_options: [], loading_options: false,
      } : prev);
    }
  };
  const cancelEditProduct = () => { setEditingPid(null); setProductForm(null); };
  const setOptGroupName = (gi, next) => setProductForm((prev) => ({
    ...prev,
    options: (prev.options || []).map((g, i) => i === gi ? { ...g, name: next } : g),
  }));
  const setOptValueLabel = (gi, vi, next) => setProductForm((prev) => ({
    ...prev,
    options: (prev.options || []).map((g, i) => i === gi ? {
      ...g,
      values: (g.values || []).map((v, j) => j === vi ? { ...v, label: next } : v),
    } : g),
  }));
  const saveEditProduct = async (g) => {
    if (!productForm) return;
    setSavingProduct(true);
    try {
      // 1) 상품명 · 옵션 rename 통합 저장 · options 가 로드됐다면 함께 전송해
      //    backend 가 old→new variant_key 이관을 수행한다.
      const productPatch = {};
      if ((productForm.name || '').trim() && productForm.name.trim() !== (g.product_name || '')) {
        productPatch.name = productForm.name.trim();
      }
      if (Array.isArray(productForm.options)) {
        productPatch.options = productForm.options;
      }
      if (Object.keys(productPatch).length) {
        await api.put(`/seller/products/${g.product_id}`, productPatch);
      }
      // 2) 이 매입처 - 이 상품의 각 variant 에 supplier_product_name + purchase_price upsert.
      //    NOTE: 옵션 rename 이 발생한 경우 서버 migration 이 product_suppliers 를 이미
      //    new variant_key 로 옮겼으므로, upsert 시 new_vk 로 이관된 variant_key 를 보내야 한다.
      //    productForm.prices 는 old_vk → price 로 유지되고 있으므로 old→new 재계산.
      const oldToNewVk = _computeVkRenameMap(
        productForm.original_options || [],
        productForm.options || [],
      );
      const spn = (productForm.supplier_product_name || '').trim() || null;
      const originalById = new Map();
      for (const it of items) {
        if (it.product_id === g.product_id) originalById.set(it.variant_key, it);
      }
      for (const [oldVk, priceRaw] of Object.entries(productForm.prices || {})) {
        const orig = originalById.get(oldVk);
        if (!orig) continue;
        const targetVk = oldToNewVk[oldVk] || oldVk;
        await api.post('/seller/product-suppliers', {
          product_id: g.product_id,
          supplier_id: sid,
          variant_key: targetVk,
          supplier_product_name: spn,
          purchase_price: Math.max(0, Number(priceRaw) || 0),
          is_primary: !!orig.is_primary,
          moq: orig.moq, order_unit: orig.order_unit,
          lead_time_days: orig.lead_time_days, safety_stock: orig.safety_stock,
          reorder_point: orig.reorder_point, supplier_sku: orig.supplier_sku,
        });
      }
      toast.success('저장되었습니다.');
      cancelEditProduct();
      await load();
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSavingProduct(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('매입처명을 입력해주세요.'); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/seller/suppliers/${sid}`, form);
      setSup(data.supplier); setEditing(false);
      toast.success('저장되었습니다.');
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };

  // 매입 상품 목록 · variant_key 를 보기 좋게 파싱 (예: "색상=블랙 · 사이즈=S" or "옵션 없음").
  //   A4-1G-2C · display alias map 이 있으면 우선 사용 (표시명 · 재고/매입처 연결은 원본 유지).
  const parseVK = (vk, dmap) => {
    if (!vk || vk === 'default') return '옵션 없음';
    const dm = dmap || {};
    return vk.split('|').map((p) => {
      const [k, v] = p.split('=');
      const gInfo = dm[k];
      const gDisp = (gInfo && gInfo.display_name) || k;
      const vDisp = (gInfo && gInfo.values && gInfo.values[v]) || v;
      return `${gDisp} · ${vDisp}`;
    }).join(' / ');
  };
  // 상품 단위 그룹핑 · 각 상품의 옵션별 공급가 나열.
  const groups = {};
  for (const it of items) {
    const pid = it.product_id;
    if (!groups[pid]) groups[pid] = { product_id: pid, product_name: it.product_name, product_sku: it.product_sku,
      product_image: it.product_image, supplier_product_name: it.supplier_product_name,
      is_primary: it.is_primary, option_display_map: it.option_display_map || {}, rows: [] };
    if (it.supplier_product_name && !groups[pid].supplier_product_name) {
      groups[pid].supplier_product_name = it.supplier_product_name;
    }
    if (it.is_primary) groups[pid].is_primary = true;
    // 첫 non-empty display map 유지 (variant 단위 동일 값)
    if (it.option_display_map && Object.keys(it.option_display_map).length &&
        !Object.keys(groups[pid].option_display_map || {}).length) {
      groups[pid].option_display_map = it.option_display_map;
    }
    groups[pid].rows.push({
      vk: it.variant_key,
      price: Number(it.purchase_price ?? 0),
      sku: it.variant_sku || null,
      barcode: it.variant_barcode || null,
    });
  }
  // A4-1G-2C · 대량 상품 대응 · 검색 (client-side · 신규 API 없음).  대상 = 상품명 · 매입처
  //   상품명 · 옵션 표시명 · SKU · 바코드.
  const [productQ, setProductQ] = useState('');
  const pq = productQ.trim().toLowerCase();
  const groupListAll = Object.values(groups);
  const groupList = !pq ? groupListAll : groupListAll.filter((g) => {
    if ((g.product_name || '').toLowerCase().includes(pq)) return true;
    if ((g.supplier_product_name || '').toLowerCase().includes(pq)) return true;
    for (const r of g.rows) {
      if ((r.sku || '').toLowerCase().includes(pq)) return true;
      if ((r.barcode || '').toLowerCase().includes(pq)) return true;
      if (parseVK(r.vk, g.option_display_map).toLowerCase().includes(pq)) return true;
    }
    return false;
  });

  const FIELDS = [
    ['name','매입처명 *'],['contact_name','담당자명'],
    ['phone','전화번호'],['kakao_id','카카오톡 연락처/ID'],
    ['email','이메일'],['address','주소'],['address_detail','상세주소'],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="supplier-detail-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center gap-2">
          <p className="text-sm font-black truncate" data-testid="supplier-detail-name">
            {sup?.name || '매입처 상세'}
            {sup?.is_active === false && <span className="ml-2 text-[10px] font-black text-gray-400">비활성</span>}
          </p>
          <div className="flex items-center gap-2">
            {!editing && !loading && (
              <button onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-black/10 dark:border-white/10 text-xs font-bold hover:border-[#FF3B30]"
                data-testid="supplier-detail-edit">
                <Edit2 size={11}/> 수정
              </button>
            )}
            <button onClick={onClose} aria-label="닫기" data-testid="supplier-detail-close"><X size={14}/></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="py-8 text-center text-gray-400"><Loader2 size={14} className="animate-spin inline"/></div>
          ) : (
            <>
              {/* 기본 정보 · 표시 또는 편집 */}
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">기본 정보</p>
                {editing ? (
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="supplier-edit-form">
                    {FIELDS.map(([k,l]) => (
                      <div key={k} className={k === 'address' || k === 'address_detail' ? 'sm:col-span-2' : ''}>
                        <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">{l}</p>
                        <input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                          data-testid={`supplier-edit-${k}`}/>
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">메모</p>
                      <textarea value={form.memo || ''} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={2}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                        data-testid="supplier-edit-memo"/>
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-2">
                      <button onClick={save} disabled={saving}
                        className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40"
                        data-testid="supplier-edit-save">
                        {saving ? '저장 중…' : '저장'}
                      </button>
                      <button onClick={() => { setEditing(false); load(); }}
                        className="px-4 py-2 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold"
                        data-testid="supplier-edit-cancel">취소</button>
                    </div>
                  </div>
                ) : (
                  <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <SupDetailRow label="담당자" value={sup?.contact_name}/>
                    <SupDetailRow label="전화번호" value={sup?.phone}/>
                    <SupDetailRow label="카카오톡" value={sup?.kakao_id}/>
                    <SupDetailRow label="이메일" value={sup?.email}/>
                    <div className="sm:col-span-2">
                      <SupDetailRow label="주소" value={[sup?.address, sup?.address_detail].filter(Boolean).join(' ') || null}/>
                    </div>
                    {sup?.memo && (
                      <div className="sm:col-span-2">
                        <SupDetailRow label="메모" value={sup?.memo}/>
                      </div>
                    )}
                  </dl>
                )}
              </div>

              {/* 이 매입처에서 매입하는 상품 */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">이 매입처에서 매입하는 상품 · {groupListAll.length}건</p>
                  {groupListAll.length > 0 && (
                    <div className="ml-auto relative">
                      <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
                      <input value={productQ} onChange={(e) => setProductQ(e.target.value)}
                        placeholder="상품명 · 매입처 상품명 · SKU · 바코드"
                        className="pl-6 pr-2 py-1 text-[11px] rounded-md border border-black/10 dark:border-white/10 bg-transparent w-52"
                        data-testid="supplier-products-search"/>
                    </div>
                  )}
                </div>
                {groupList.length === 0 ? (
                  <div className="py-8 text-center bg-black/[0.02] rounded-xl text-xs text-gray-400" data-testid="supplier-products-empty">
                    {pq ? `"${productQ}" 검색 결과가 없습니다.` : (
                      <>
                        아직 이 매입처에 연결된 상품이 없습니다.
                        <p className="mt-1 text-[10px]">상품 등록/수정 화면에서 매입처를 연결하면 여기에 표시됩니다.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid="supplier-products-list">
                    <div className="hidden sm:grid gap-2 px-3 py-2 text-[10px] font-black uppercase text-gray-500 bg-black/[0.02]"
                         style={{ gridTemplateColumns: '48px repeat(14, minmax(0,1fr))' }}>
                      <span>사진</span>
                      <span className="col-span-3">상품명</span>
                      <span className="col-span-2">매입처 상품명</span>
                      <span className="col-span-2">옵션</span>
                      <span className="col-span-3">SKU · 바코드</span>
                      <span className="col-span-2 text-right">공급가</span>
                      <span className="col-span-2 text-right">&nbsp;</span>
                    </div>
                    {/* A4-1G-2C · 대량 상품 대응 · 내부 vertical scroll (max-h-96) · modal 전체 높이 폭주 방지 */}
                    <div className="divide-y divide-black/5 dark:divide-white/5 max-h-[26rem] overflow-y-auto"
                         data-testid="supplier-products-scroll">
                      {groupList.map((g) => {
                        const inEdit = editingPid === g.product_id && productForm;
                        return (
                        <div key={g.product_id} className="px-3 py-2" data-testid={`supplier-product-${g.product_id}`}>
                          {!inEdit ? (
                            <>
                              {g.rows.map((r, ri) => (
                                <div key={r.vk}
                                     className="grid gap-2 text-xs items-center py-0.5"
                                     style={{ gridTemplateColumns: '48px repeat(14, minmax(0,1fr))' }}>
                                  <div>
                                    {ri === 0 && (
                                      // A4-1G-2C · 기존 seller_products 대표이미지 REUSE · object-contain · 원본 비율 유지.
                                      <div className="w-10 h-10 rounded bg-black/5 dark:bg-white/5 overflow-hidden flex items-center justify-center"
                                           data-testid={`supplier-product-thumb-${g.product_id}`}>
                                        {g.product_image ? (
                                          <img src={g.product_image} alt="" className="w-full h-full object-contain" loading="lazy"/>
                                        ) : (
                                          <span className="text-[9px] text-gray-400">no img</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="col-span-14 sm:col-span-3">
                                    {ri === 0 && (
                                      <span className="font-bold">
                                        {g.product_name || g.product_id}
                                        {g.is_primary && <span className="ml-1 px-1 rounded bg-[#FF3B30] text-white text-[9px] font-black">주</span>}
                                      </span>
                                    )}
                                  </div>
                                  <div className="col-span-7 sm:col-span-2 text-gray-500 truncate" title={g.supplier_product_name || ''}>
                                    {ri === 0 ? (g.supplier_product_name || '—') : ''}
                                  </div>
                                  <div className="col-span-7 sm:col-span-2 text-gray-600 dark:text-gray-400 truncate" title={parseVK(r.vk, g.option_display_map)}>
                                    {parseVK(r.vk, g.option_display_map)}
                                  </div>
                                  <div className="col-span-14 sm:col-span-3 text-[10px] font-mono text-gray-500 leading-tight"
                                       data-testid={`supplier-product-${g.product_id}-ids-${r.vk}`}>
                                    <div className="truncate" title={`SKU: ${r.sku || '-'}`}>SKU · {r.sku || <span className="text-gray-400">—</span>}</div>
                                    <div className="truncate" title={`Barcode: ${r.barcode || '-'}`}>BC &nbsp;· {r.barcode || <span className="text-gray-400">—</span>}</div>
                                  </div>
                                  <div className="col-span-7 sm:col-span-2 text-right font-bold" data-testid={`supplier-product-${g.product_id}-price-${r.vk}`}>
                                    {won(r.price)}
                                  </div>
                                  <div className="col-span-7 sm:col-span-2 text-right">
                                    {ri === 0 && (
                                      <button type="button" onClick={() => beginEditProduct(g)}
                                        className="text-[11px] font-bold text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1"
                                        data-testid={`supplier-product-edit-${g.product_id}`}>
                                        <Edit2 size={10}/> 수정
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="space-y-2" data-testid={`supplier-product-edit-form-${g.product_id}`}>
                              <div className="grid sm:grid-cols-2 gap-2">
                                <div>
                                  <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">상품명</p>
                                  <input value={productForm.name}
                                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                                    className="w-full px-2 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent"
                                    data-testid={`supplier-product-edit-name-${g.product_id}`}/>
                                  <p className="text-[9px] text-gray-400 mt-0.5">※ seller_products.name 변경 · 모든 화면에 반영</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">매입처 상품명</p>
                                  <input value={productForm.supplier_product_name}
                                    onChange={(e) => setProductForm({ ...productForm, supplier_product_name: e.target.value })}
                                    className="w-full px-2 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent"
                                    data-testid={`supplier-product-edit-spn-${g.product_id}`}/>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-black uppercase text-gray-500 mb-1">옵션 · 옵션값 · 공급가</p>
                                {productForm.loading_options ? (
                                  <div className="py-3 text-center text-[11px] text-gray-400" data-testid={`supplier-product-edit-options-loading-${g.product_id}`}>
                                    <Loader2 size={11} className="animate-spin inline mr-1"/> 옵션 로드 중…
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {/* A4-1G-2D · 옵션 그룹명(색상/사이즈 등) 직접 편집 */}
                                    {(productForm.options || []).length > 0 && (
                                      <div className="grid gap-2"
                                           style={{ gridTemplateColumns: `repeat(${Math.max(1, productForm.options.length)}, minmax(0,1fr))` }}
                                           data-testid={`supplier-product-edit-groups-${g.product_id}`}>
                                        {(productForm.options || []).map((opt, gi) => (
                                          <div key={`grp-${gi}`}>
                                            <p className="text-[9px] font-black uppercase text-gray-400 mb-0.5">옵션 그룹 {gi + 1}</p>
                                            <input value={opt.name || ''}
                                              onChange={(e) => setOptGroupName(gi, e.target.value)}
                                              placeholder="예: 색상"
                                              className="w-full px-2 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent"
                                              data-testid={`supplier-product-edit-group-name-${g.product_id}-${gi}`}/>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {/* variant 단위 옵션값 라벨 · SKU · 공급가 (SKU/Barcode 는 조회 전용) */}
                                    {g.rows.map((r) => {
                                      // find (gi, vi) for this row's variant_key against ORIGINAL options snapshot
                                      const origParts = (r.vk && r.vk !== 'default')
                                        ? r.vk.split('|').map((p) => { const i = p.indexOf('='); return i > 0 ? [p.slice(0, i), p.slice(i + 1)] : null; }).filter(Boolean)
                                        : [];
                                      // Build editable label inputs per group dimension (positional match to original_options)
                                      const inputsPerGroup = (productForm.original_options || []).map((ogrp, gi) => {
                                        const origLabel = (origParts.find(([k]) => k === ogrp.name) || [])[1] || '';
                                        const vi = (ogrp.values || []).findIndex((v) => (v?.label || '') === origLabel);
                                        if (vi < 0) return null;
                                        const cur = ((productForm.options?.[gi]?.values || [])[vi] || {}).label || '';
                                        return { gi, vi, cur };
                                      });
                                      return (
                                        <div key={r.vk} className="grid grid-cols-12 gap-2 items-center py-0.5"
                                             data-testid={`supplier-product-edit-row-${g.product_id}-${r.vk}`}>
                                          <div className="col-span-5 flex gap-1">
                                            {inputsPerGroup.map((info, idx) => info ? (
                                              <input key={`${info.gi}-${info.vi}`} value={info.cur}
                                                onChange={(e) => setOptValueLabel(info.gi, info.vi, e.target.value)}
                                                className="flex-1 px-2 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent"
                                                placeholder={`값 ${idx + 1}`}
                                                data-testid={`supplier-product-edit-label-${g.product_id}-${info.gi}-${info.vi}`}/>
                                            ) : (
                                              <span key={`na-${idx}`} className="flex-1 text-[10px] text-gray-400 py-1.5">—</span>
                                            ))}
                                            {inputsPerGroup.every((x) => !x) && (
                                              <span className="text-[11px] text-gray-500 py-1.5">옵션 없음</span>
                                            )}
                                          </div>
                                          <span className="col-span-3 text-[10px] font-mono text-gray-400 truncate" title={`SKU ${r.sku || '-'} / BC ${r.barcode || '-'}`}>
                                            {r.sku || '—'}
                                          </span>
                                          <div className="col-span-4 relative">
                                            <input type="number" min="0"
                                              value={productForm.prices?.[r.vk] ?? 0}
                                              onChange={(e) => setProductForm({
                                                ...productForm,
                                                prices: { ...(productForm.prices || {}), [r.vk]: Math.max(0, Number(e.target.value) || 0) },
                                              })}
                                              className="w-full pl-2 pr-6 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent"
                                              data-testid={`supplier-product-edit-price-${g.product_id}-${r.vk}`}/>
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">원</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-400">
                                ※ 옵션 그룹명(색상/사이즈)과 옵션값(블랙/레드)을 수정해도 <b>SKU · 바코드 · 재고 · 예약재고 · 매입처 연결 · 공급가</b>는 그대로 유지됩니다.
                              </p>
                              <p className="text-[10px] text-gray-400">
                                ※ SKU · 바코드는 상품 등록 시 자동 생성된 값이며 이 화면에서는 조회 전용입니다.
                              </p>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => saveEditProduct(g)} disabled={savingProduct}
                                  className="flex-1 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40"
                                  data-testid={`supplier-product-edit-save-${g.product_id}`}>
                                  {savingProduct ? '저장 중…' : '저장'}
                                </button>
                                <button type="button" onClick={cancelEditProduct}
                                  className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold"
                                  data-testid={`supplier-product-edit-cancel-${g.product_id}`}>취소</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );})}
                    </div>
                  </div>
                )}
              </div>

              <p className="text-[10px] text-gray-400">
                ※ 매입처 삭제는 과거 발주/거래/재고 참조 데이터를 훼손할 수 있어 이번 STEP 에서 제공하지 않습니다 (DEFERRED).
                필요 시 상세에서 관리자 요청으로 비활성 처리해 주세요.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SupDetailRow({ label, value }) {
  return (
    <div className="flex gap-2 text-sm">
      <dt className="text-[10px] font-black uppercase text-gray-400 w-16 flex-shrink-0 pt-0.5">{label}</dt>
      <dd className="flex-1 text-black dark:text-white break-all">{value || <span className="text-gray-400">—</span>}</dd>
    </div>
  );
}
